<template>
	<!-- DC: 고객카드 '대화 자료'. 카카오톡 대화 내보내기(txt)와 통화 녹음을 고객에게 쌓는다(받아쓰기·요약은 다음 단계).
	     목록은 고객카드를 볼 수 있는 직원 모두, 내용(메시지·녹음)은 그 고객의 담당자·올린 사람·관리자만 연다(routes/talk.js). -->
	<div class="ctp">
		<div class="ctp-bar">
			<span class="ctp-sum" v-if="loaded">
				카톡 {{ counts.kakao }}개 파일 · 메시지 {{ uniqueMsgs }}건(겹친 메시지는 한 번) · 통화 녹음 {{ counts.call }}건
			</span>
			<span class="ctp-sum" v-else-if="failed">
				목록을 불러오지 못했습니다.
				<button type="button" class="btn btn-xxsm btn-light" @click="load">다시 시도</button>
			</span>
			<span class="ctp-sum" v-else>불러오는 중</span>
			<div class="ctp-bar__btns">
				<button type="button" class="btn btn-sm btn-secondary" :disabled="busy" @click="pickKakao">카톡 대화 올리기</button>
				<button type="button" class="btn btn-sm btn-secondary" :disabled="busy" @click="pickCall">통화 녹음 올리기</button>
				<input ref="kakaoInput" type="file" accept=".txt,.csv,text/plain,text/csv" class="ctp-hidden" @change="onKakao" />
				<input ref="callInput" type="file" accept="audio/*,.m4a,.amr,.3gp,.3ga,.aac,.wav,.ogg,.opus,.mp3" class="ctp-hidden" @change="onCall" />
			</div>
		</div>
		<div class="ctp-help">
			※ 카톡: 대화방 메뉴의 '대화 내보내기'로 받은 txt(맥은 csv)를 올립니다. 같은 대화를 나중에 다시 내보내 올려도 겹친 메시지는
			한 번으로 셉니다. 통화 녹음: 휴대폰 녹음 파일(m4a, amr 등)을 올리면 통화 시각과 길이를 함께 적습니다.
			내용은 이 고객의 담당자와 올린 사람, 관리자만 엽니다.
		</div>
		<div v-if="progress != null" class="ctp-progress">올리는 중 {{ progress }}%</div>

		<table class="table ctp-list">
			<colgroup>
				<col width="90" />
				<col />
				<col width="150" />
				<col width="130" />
			</colgroup>
			<tr>
				<th>종류</th>
				<th>내용</th>
				<th>올린 사람</th>
				<th></th>
			</tr>
			<tr v-for="f in list" :key="'tf_' + f.file_uid">
				<td>{{ f.kind === 'kakao' ? '카톡' : '통화 녹음' }}</td>
				<td class="txt-left">
					<template v-if="f.kind === 'kakao'">
						<b>{{ f.room_title || f.org_name }}</b>
						<span class="ctp-sub">메시지 {{ f.msg_count }}건<template v-if="f.msg_new !== f.msg_count"> (새 메시지 {{ f.msg_new }}건)</template></span>
						<span class="ctp-sub d-block">{{ f.first_at || '-' }} ~ {{ f.last_at || '-' }}<template v-if="f.staff_speaker"> · 직원 쪽 {{ f.staff_speaker }}</template></span>
					</template>
					<template v-else>
						<b>{{ f.call_at || '통화 시각 모름' }}</b>
						<span class="ctp-sub">{{ dur(f.duration_sec) }}<template v-if="f.counterpart"> · {{ f.counterpart }}</template></span>
						<span class="ctp-sub d-block">{{ f.org_name }} · {{ size(f.size_bytes) }}<template v-if="f.memo"> · {{ f.memo }}</template></span>
						<audio v-if="audio && audio.uid === f.file_uid" :src="audio.url" controls autoplay class="ctp-audio"></audio>
					</template>
				</td>
				<td>
					{{ f.reg_name || '-' }}
					<span class="ctp-sub d-block">{{ f.reg_at }}</span>
				</td>
				<td class="ctp-btns">
					<template v-if="f.can_open">
						<button v-if="f.kind === 'kakao'" type="button" class="btn btn-xxsm btn-light" @click="openMsgs(f)">대화 보기</button>
						<button v-else type="button" class="btn btn-xxsm btn-light" :disabled="audioBusy" @click="play(f)">듣기</button>
					</template>
					<span v-else class="ctp-sub" title="내용은 이 고객의 담당자와 올린 사람, 관리자만 엽니다">내용 비공개</span>
					<button v-if="f.can_delete" type="button" class="btn btn-xxsm btn-light" :disabled="busy" @click="remove(f)">지우기</button>
				</td>
			</tr>
			<tr v-if="loaded && !list.length">
				<td colspan="4">올린 대화 자료가 없습니다.</td>
			</tr>
		</table>
		<div class="btn-wrap" v-if="loaded && more">
			<button type="button" class="btn btn-sm btn-light" :disabled="moreBusy" @click="loadMore">더 보기</button>
		</div>

		<!-- 통화 녹음 정보 -->
		<div v-if="callForm" class="popup-wrap">
			<div class="dim" @click="callForm = null"></div>
			<div class="popup sm">
				<div class="popup-tit">
					통화 녹음 올리기
					<button type="button" class="btn btn-close" @click="callForm = null"><b-icon-x-lg /></button>
				</div>
				<div class="popup-con">
					<table class="table type-input">
						<colgroup><col width="30%" /><col width="70%" /></colgroup>
						<tr>
							<th>파일</th>
							<td class="txt-left">{{ callForm.file.name }} <span class="ctp-sub">{{ size(callForm.file.size) }}</span></td>
						</tr>
						<tr>
							<th>통화 일시</th>
							<td class="txt-left">
								<input type="datetime-local" class="form-control" v-model="callForm.callAt" />
								<span class="ctp-sub d-block">비우면 파일 이름에서 읽습니다(삼성 녹음 등).</span>
							</td>
						</tr>
						<tr>
							<th>통화 길이</th>
							<td class="txt-left">{{ callForm.durationSec == null ? '파일에서 읽지 못함' : dur(callForm.durationSec) }}</td>
						</tr>
						<tr>
							<th>상대</th>
							<td><input type="text" class="form-control" maxlength="100" placeholder="비우면 파일 이름에서 읽습니다" v-model="callForm.counterpart" /></td>
						</tr>
						<tr>
							<th>메모</th>
							<td><input type="text" class="form-control" maxlength="500" v-model="callForm.memo" /></td>
						</tr>
					</table>
					<div class="btn-wrap">
						<button type="button" class="btn btn-sm btn-secondary" @click="callForm = null">취소</button>
						<button type="button" class="btn btn-sm btn-primary" :disabled="busy" @click="sendCall">올리기</button>
					</div>
				</div>
			</div>
		</div>

		<!-- 카톡 대화 보기 -->
		<div v-if="view" class="popup-wrap">
			<div class="dim" @click="closeView"></div>
			<div class="popup lg">
				<div class="popup-tit">
					{{ view.file.room_title || view.file.org_name }}
					<button type="button" class="btn btn-close" @click="closeView"><b-icon-x-lg /></button>
				</div>
				<div class="popup-con">
					<div class="ctp-view-bar">
						<span>메시지 {{ view.file.msg_count }}건 · {{ view.file.first_at || '-' }} ~ {{ view.file.last_at || '-' }}</span>
						<label class="ctp-view-staff">
							직원 쪽 이름
							<select class="form-control sm select" :value="view.file.staff_speaker || ''" :disabled="busy" @change="setSpeaker($event)">
								<option value="">모름</option>
								<option v-for="s in view.speakers" :key="'sp_' + s.name" :value="s.name">{{ s.name }} ({{ s.count }})</option>
							</select>
						</label>
					</div>
					<div class="ctp-msgs">
						<table class="table ctp-msg-tbl">
							<colgroup>
								<col width="140" />
								<col width="130" />
								<col />
							</colgroup>
							<tr v-for="m in view.list" :key="'msg_' + m.seq" v-bind:class="{ 'is-staff': m.is_staff, 'is-system': m.kind === 'system' }">
								<td class="ctp-sub">{{ m.sent_at.slice(0, 16) }}</td>
								<td class="txt-left"><b>{{ m.speaker || '-' }}</b></td>
								<td class="txt-left ctp-body">
									<span v-if="KIND[m.kind] && m.body !== KIND[m.kind]" class="ctp-kind">{{ KIND[m.kind] }}</span>
									{{ m.body }}
								</td>
							</tr>
						</table>
						<div class="btn-wrap" v-if="view.more">
							<button type="button" class="btn btn-sm btn-light" :disabled="busy" @click="moreMsgs">더 보기</button>
						</div>
					</div>
					<div class="btn-wrap">
						<button type="button" class="btn btn-sm btn-secondary" @click="closeView">닫기</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
const KIND = { photo: '사진', video: '동영상', file: '파일', emoticon: '이모티콘', deleted: '삭제됨', system: '안내' };
const PAGE = 300;
// 올리기·지우기가 끝나면 이 이름으로 알린다. 올리는 사이 다른 고객으로 갔다 돌아오면 패널이 새로 만들어져,
// 끝난 요청을 낸 옛 패널이 아니라 지금 보이는 패널이 목록을 다시 읽어야 한다(codex 리뷰).
const CHANGED = 'weonus:talk-changed';

export default {
	name: 'ClientTalkPanel',
	props: {
		cid: { type: [Number, String], required: true },
	},
	data() {
		return {
			KIND,
			loaded: false,
			failed: false,
			busy: false,
			list: [],
			more: false,
			moreBusy: false,
			counts: { kakao: 0, call: 0 },
			uniqueMsgs: 0,
			limits: { kakao_mb: 20, call_mb: 200 },
			progress: null,
			callForm: null,
			view: null,
			audio: null,
			audioBusy: false,
			seq: 0,
			viewSeq: 0,
			audioSeq: 0,
			alive: true,
		};
	},
	watch: {
		// 고객카드의 이전·다음은 같은 화면에서 고객만 바꾼다. 앞 고객의 창과 녹음을 남기지 않는다.
		cid() {
			this.callForm = null;
			this.closeView();
			this.stopAudio();
			this.list = [];
			this.more = false;
			this.loaded = false;
			this.load();
		},
	},
	created() {
		this.onChanged = (e) => {
			if (e && e.detail && String(e.detail.cid) === String(this.cid)) this.load();
		};
		window.addEventListener(CHANGED, this.onChanged);
		this.load();
	},
	beforeUnmount() {
		// 떠난 뒤 도착한 응답은 버린다(녹음을 받아 Blob 주소를 만들면 해제할 곳이 없다).
		this.alive = false;
		window.removeEventListener(CHANGED, this.onChanged);
		this.closeView();
		this.stopAudio();
	},
	methods: {
		notifyChanged(cid) {
			window.dispatchEvent(new CustomEvent(CHANGED, { detail: { cid } }));
		},
		async load() {
			const seq = ++this.seq;
			const cid = this.cid;
			this.failed = false;
			const re = await this.$apiGET('/admin/api/user/admin/talk/list?cid=' + encodeURIComponent(cid));
			if (!this.alive || seq !== this.seq) return;
			if (!re || !re.list) {
				// 실패 안내는 API 계층이 띄운다. 여기서는 '불러오는 중' 에 멈추지 않고 다시 시도를 보인다.
				this.loaded = false;
				this.failed = true;
				return;
			}
			this.list = re.list;
			this.more = !!re.more;
			this.counts = re.counts || { kakao: 0, call: 0 };
			this.uniqueMsgs = re.unique_msgs || 0;
			if (re.limits) this.limits = re.limits;
			this.loaded = true;
		},
		async loadMore() {
			const seq = this.seq;
			const cid = this.cid;
			this.moreBusy = true;
			try {
				const re = await this.$apiGET(`/admin/api/user/admin/talk/list?cid=${encodeURIComponent(cid)}&offset=${this.list.length}`);
				if (!this.alive || seq !== this.seq || !re || !re.list) return;
				const have = new Set(this.list.map((f) => f.file_uid));
				this.list = this.list.concat(re.list.filter((f) => !have.has(f.file_uid)));
				this.more = !!re.more;
			} finally {
				this.moreBusy = false;
			}
		},
		dur(sec) {
			if (sec == null) return '길이 모름';
			const s = Math.round(Number(sec));
			const h = Math.floor(s / 3600);
			const m = Math.floor((s % 3600) / 60);
			if (h) return `${h}시간 ${m}분`;
			return m ? `${m}분 ${s % 60}초` : `${s % 60}초`;
		},
		size(b) {
			const n = Number(b) || 0;
			if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)}MB`;
			return `${Math.max(1, Math.round(n / 1024))}KB`;
		},
		pickKakao() {
			this.$refs.kakaoInput.value = '';
			this.$refs.kakaoInput.click();
		},
		pickCall() {
			this.$refs.callInput.value = '';
			this.$refs.callInput.click();
		},
		async upload(path, fields, file) {
			this.busy = true;
			this.progress = 0;
			try {
				return await this.$apiFORM(path, fields, file, {
					onProgress: (e) => {
						if (e && e.total) this.progress = Math.min(99, Math.round((e.loaded / e.total) * 100));
					},
				});
			} finally {
				this.busy = false;
				this.progress = null;
			}
		},
		async onKakao(ev) {
			const file = ev.target.files && ev.target.files[0];
			if (!file) return;
			if (file.size > this.limits.kakao_mb * 1024 * 1024) return alert(`파일이 너무 큽니다. 최대 ${this.limits.kakao_mb}MB 까지 올릴 수 있습니다.`);
			const cid = this.cid;
			const re = await this.upload('/admin/api/user/admin/talk/kakao', { cid }, file);
			if (!re || !re.ok) return;
			const who = re.staff_speaker ? `직원 쪽은 '${re.staff_speaker}' 으로 보았습니다.` : '직원 쪽 이름을 찾지 못했습니다. 대화 보기에서 고를 수 있습니다.';
			const notes = [
				re.truncated ? '메시지가 너무 많아 앞부분만 담았습니다.' : '',
				re.body_cut ? `아주 긴 메시지 ${re.body_cut}건은 앞부분만 담았습니다.` : '',
			].filter(Boolean).join('\n');
			this.notifyChanged(cid);
			alert(`메시지 ${re.msg_count}건을 담았습니다(새 메시지 ${re.msg_new}건).\n${re.first_at || '-'} ~ ${re.last_at || '-'}\n${who}${notes ? '\n' + notes : ''}`);
		},
		onCall(ev) {
			const file = ev.target.files && ev.target.files[0];
			if (!file) return;
			if (file.size > this.limits.call_mb * 1024 * 1024) return alert(`파일이 너무 큽니다. 최대 ${this.limits.call_mb}MB 까지 올릴 수 있습니다.`);
			this.callForm = { file, cid: this.cid, callAt: '', durationSec: null, counterpart: '', memo: '' };
			// 반응형 사본에 써야 화면이 바뀐다(원본 객체에 쓰면 길이가 '읽지 못함' 으로 남는다).
			const form = this.callForm;
			// 길이는 브라우저가 파일 머리에서 읽는다(amr 처럼 브라우저가 못 여는 형식은 비워 둔다).
			try {
				const url = URL.createObjectURL(file);
				const a = new Audio();
				a.preload = 'metadata';
				a.onloadedmetadata = () => {
					if (Number.isFinite(a.duration) && a.duration > 0) form.durationSec = Math.round(a.duration);
					URL.revokeObjectURL(url);
				};
				a.onerror = () => URL.revokeObjectURL(url);
				a.src = url;
			} catch (e) { /* 길이 없이 올린다 */ }
		},
		async sendCall() {
			const f = this.callForm;
			if (!f || this.busy) return;
			const re = await this.upload('/admin/api/user/admin/talk/call', {
				cid: f.cid,
				callAt: f.callAt ? f.callAt.replace('T', ' ') : '',
				durationSec: f.durationSec,   // 못 읽었으면(null) 보내지 않는다. 서버는 '모름' 으로 둔다
				counterpart: f.counterpart,
				memo: f.memo,
			}, f.file);
			if (!re || !re.ok) return;
			if (this.alive && this.callForm === f) this.callForm = null;
			this.notifyChanged(f.cid);
			if (!re.call_at) alert('통화 시각을 파일 이름에서 찾지 못했습니다. 목록에는 시각 없이 남습니다.');
		},
		closeView() {
			// 닫은 뒤 도착한 '대화 보기' 응답이 창을 다시 열지 않게 번호를 넘긴다.
			this.viewSeq += 1;
			this.view = null;
		},
		async openMsgs(f) {
			const seq = ++this.viewSeq;
			const cid = this.cid;
			const re = await this.$apiGET(`/admin/api/user/admin/talk/msgs?fileUid=${f.file_uid}&limit=${PAGE}`);
			if (!this.alive || seq !== this.viewSeq || String(cid) !== String(this.cid) || !re || !re.list) return;
			this.view = { file: re.file, speakers: re.speakers || [], list: re.list, more: !!re.more };
		},
		async moreMsgs() {
			const v = this.view;
			if (!v || !v.list.length) return;
			const after = v.list[v.list.length - 1].seq;
			this.busy = true;
			try {
				const re = await this.$apiGET(`/admin/api/user/admin/talk/msgs?fileUid=${v.file.file_uid}&after=${after}&limit=${PAGE}`);
				if (!re || !re.list || this.view !== v) return;
				v.list = v.list.concat(re.list);
				v.more = !!re.more;
			} finally {
				this.busy = false;
			}
		},
		async setSpeaker(ev) {
			const v = this.view;
			const speaker = ev.target.value || null;
			this.busy = true;
			try {
				const re = await this.$apiPOST('/admin/api/user/admin/talk/speaker', { fileUid: v.file.file_uid, speaker });
				if (!re || !re.ok) {
					ev.target.value = v.file.staff_speaker || '';
					return;
				}
				v.file.staff_speaker = speaker;
				v.list = v.list.map((m) => ({ ...m, is_staff: !!speaker && m.speaker === speaker }));
				if (this.alive) this.load();
			} finally {
				this.busy = false;
			}
		},
		stopAudio() {
			this.audioSeq += 1;   // 받는 중인 녹음은 도착해도 버린다
			if (this.audio) URL.revokeObjectURL(this.audio.url);
			this.audio = null;
		},
		async play(f) {
			if (this.audio && this.audio.uid === f.file_uid) return;
			this.stopAudio();
			const seq = this.audioSeq;
			this.audioBusy = true;
			try {
				// <audio> 는 인증 헤더를 실을 수 없어 받아서 재생한다(원본 주소를 화면에 두지 않는다).
				const buf = await this.$apiDOWN(`/admin/api/user/admin/talk/file?fileUid=${f.file_uid}`, 'audio/*');
				if (!buf || !this.alive || seq !== this.audioSeq) return;
				const url = URL.createObjectURL(new Blob([buf], { type: f.mime || 'audio/mp4' }));
				this.audio = { uid: f.file_uid, url };
			} finally {
				this.audioBusy = false;
			}
		},
		async remove(f) {
			const what = f.kind === 'kakao' ? `카톡 대화(메시지 ${f.msg_count}건)` : '통화 녹음';
			if (!confirm(`이 ${what}을(를) 지웁니다. 원본 파일과 내용이 지워지고 되돌릴 수 없습니다.`)) return;
			this.busy = true;
			try {
				const cid = this.cid;
				const re = await this.$apiPOST('/admin/api/user/admin/talk/delete', { fileUid: f.file_uid });
				if (!re || !re.ok) return;
				if (this.alive && this.audio && this.audio.uid === f.file_uid) this.stopAudio();
				this.notifyChanged(cid);
			} finally {
				this.busy = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/design-tokens.scss";

.ctp-bar {
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 6px;
}
.ctp-bar__btns {
	margin-left: auto;
	display: inline-flex;
	gap: 6px;
}
.ctp-hidden {
	display: none;
}
.ctp-sum {
	color: $dt-color-ink-muted;
}
.ctp-help,
.ctp-sub {
	@include dt-type-dense;
	color: $dt-color-ink-faint;
}
.ctp-help {
	margin-bottom: 8px;
}
.ctp-progress {
	margin-bottom: 6px;
	color: $dt-color-primary-ink;
	font-weight: 600;
}
.ctp-list td.txt-left,
.ctp-msg-tbl td.txt-left {
	text-align: left;
}
.ctp-btns {
	white-space: nowrap;
	.btn + .btn {
		margin-left: 4px;
	}
}
.ctp-audio {
	display: block;
	width: 100%;
	max-width: 420px;
	height: 32px;
	margin-top: 4px;
}
.ctp-view-bar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 8px;
	color: $dt-color-ink-muted;
}
.ctp-view-staff {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin: 0;
	.form-control {
		width: 180px;
	}
}
.ctp-msgs {
	max-height: 60vh;
	overflow-y: auto;
}
.ctp-msg-tbl {
	td {
		vertical-align: top;
	}
	tr.is-staff td {
		background: $dt-color-surface-sunken;
	}
	tr.is-system td {
		color: $dt-color-ink-faint;
	}
}
.ctp-body {
	white-space: pre-wrap;
	word-break: break-word;
}
.ctp-kind {
	@include dt-type-dense;
	color: $dt-color-ink-faint;
	margin-right: 4px;
}
</style>
